/*******************************************************
 * Copyright (C) 2010-Present Avant Assessment
 * All Rights Reserved
 *******************************************************/

import * as React from "react"
import MaterialTable, {Column} from "material-table"
import {reportsStore} from "../../../reports/common/stores/ReportStore"
import {AvantColors} from "../../../../styles/AvantColors"
import {Box, makeStyles} from "@material-ui/core"
import {ReportProps} from "../../ReportData"
import {AvantVerticalDivider} from "../../../generic-components/AvantVerticalDivider"
import {useEffect, useRef, useState} from "react"
import {theme} from "../../../../styles/MuiThemes"
import {ReportTakeRow} from "../../../reports/class/models/ReportTakeRow"
import {PanelSkillEnum} from "../../../../types/types"
import testTakerSortableTableStore from "./TestTakerSortableTableStore"
import {takeSearchStore} from "../../../common/take/TakeSearchStore"
import {buildReportColumns, SkillColumnData} from "./Columns"
import {ProctorTypes} from "../../../../util/Constants"
import {sidebarStore} from "../testTakerSidebar/SideBarStore"
import {observer} from "mobx-react-lite"

export interface PanelSkill {
    symbol: string,
    name: string
}

interface TestTakerSortableTableProps {
    reportProps: ReportProps
    isMultiLanguage: boolean
}

interface TableDataRow extends ReportTakeRow {
   tableData: {
       id: number,
       checked: boolean,
   }
}

const useScrollEffectStyles: React.CSSProperties = {
    width: 25,
    height: "calc(100% - 140px)",
    position: "absolute",
    transition: "0.1s",
    pointerEvents: "none",
}

const tableStyles = makeStyles(theme => ({
    table: {
        '& tbody > .MuiTableHead-root': {
            fontSize: "1.5rem",
            height: 52,
            padding: theme.spacing(0, 1),
            whiteSpace: "nowrap",
        },
        '& tbody > .MuiTableRow-root': {
            '&:nth-child(odd)': {
                backgroundColor: AvantColors.REPORT_BACKGROUND_GRAY_DARK,
            },
            '&:hover': {
                cursor: "default !important",
            }
        },
        '& tbody > .MuiTableCell-root': {
            '&:hover': {
                cursor: "default !important",
            },
        },
    }
}))

export function openSidebarWithId(
    row: ReportTakeRow,
    skill?: PanelSkillEnum,
    selfEval?: boolean,
) {
    return () => {
        sidebarStore.takeId = row.takeId
        sidebarStore.openPanel = skill
        sidebarStore.openSelfEval = selfEval
        sidebarStore.openP360 = row.proctortype === ProctorTypes.P360.valueOf()
        sidebarStore.P360Data = row.examData
        sidebarStore.isPlaceC = row.product_package === "pl-c"
        testTakerSortableTableStore.selectedRow = row
        takeSearchStore.findSidebarInfoForTake(row.takeId)
    }
}

const TestTakerSortableTable = observer(({reportProps, isMultiLanguage}: TestTakerSortableTableProps) => {
    const [reportTableRows, setReportTableRows] = useState<TableDataRow[]>([])
    const [columns, setColumns] = useState<{left: Column<any>[], right: Column<any>[]}>({left: [], right:[]})
    const {printOptions} = reportProps
    const leftTableWidth = printOptions ? 280 : 424
    const tableRef = useRef<MaterialTable<TableDataRow>>(null)
    const leftRef = useRef<HTMLDivElement>(null)
    const rightRef = useRef<HTMLDivElement>(null)
    const classes = tableStyles()

    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        const element = e.currentTarget
        const left = leftRef.current!
        const right = rightRef.current!

        // left gradient opacity
        left.style.opacity = "0.25"
        if (element.scrollLeft < 100) {
            left.style.opacity = (element.scrollLeft / 400).toString()
        }

        // right gradient opacity
        right.style.opacity = "0.25"
        if ((element.scrollWidth - element.clientWidth - element.scrollLeft) < 100) {
            right.style.opacity = ((element.scrollWidth - element.clientWidth - element.scrollLeft) / 400).toString()
        }
    }

    useEffect(() => {
        const tableRows: TableDataRow[] = []
        const takeRows: ReportTakeRow[] = reportsStore.reportTableData!.reportTakeRows
        takeRows.forEach((row: ReportTakeRow, index: number) => {
            tableRows.push({
                ...row,
                tableData: {
                    id: index,
                    checked: false,
                },
            })
        })

        // Filter out scaled columns if there aren't any scores in the list
        if (!tableRows.some((row: TableDataRow) => row.readingScaledScore)) {
            const index: number = reportProps.skillColumns.map((col: SkillColumnData) => col.scaledScoreKey).indexOf("readingScaledScore")
            index >= 0 && reportProps.skillColumns.splice(index, 1)
        }
        if (!tableRows.some((row: TableDataRow) => row.listeningScaledScore)) {
            const index: number = reportProps.skillColumns.map((col: SkillColumnData) => col.scaledScoreKey).indexOf("listeningScaledScore")
            index >= 0 && reportProps.skillColumns.splice(index, 1)
        }
        const reportColumns = buildReportColumns(reportProps.skillColumns)

        setReportTableRows(tableRows)
        setColumns(reportColumns)
    }, [])

    if (!columns || !reportTableRows) return <></>

    // This is called when the use clicks anywhere on a row (not including the sidebar links, individual report link, etc)
    const onRowClick = (event?: React.MouseEvent<Element, MouseEvent>, rowData?: TableDataRow) => {
        if (rowData) {
            updateRow(rowData)
        }
    }

    // This is called when the user clicks on the checkboxes
    const onSelectionChange = (data: TableDataRow[]) => {
        data.forEach((row:TableDataRow) => {
            updateSelectedIds(row)
        })

        refreshTable()
    }

    const updateRow = (rowData: TableDataRow) => {
        const row: TableDataRow = reportTableRows[rowData.tableData.id]
        row.tableData.checked = !row.tableData.checked
        setReportTableRows(reportTableRows)

        updateSelectedIds(rowData)
        refreshTable()
    }

    const updateSelectedIds = (rowData: TableDataRow) => {
        const selectedIds = [...reportsStore.selectedTakeIds]
        const takeIdIndex = selectedIds.indexOf(rowData.takeId)
        if (takeIdIndex === -1) {
            selectedIds.push(rowData.takeId)
        } else {
            selectedIds.splice(takeIdIndex, 1)
        }

        reportsStore.selectedTakeIds = selectedIds

        tableRef.current && tableRef.current.setState((prevState: TableDataRow[]) => {
            prevState[rowData.tableData.id] = rowData
        })
    }

    const refreshTable = () => {
        tableRef.current!.forceUpdate()
    }

    return (
        <Box display={"flex"} style={{overflowX: "hidden"}} className={classes.table}>
            <MaterialTable
                columns={columns.left}
                data={reportTableRows}
                tableRef={tableRef}
                onRowClick={onRowClick}
                onSelectionChange={onSelectionChange}
                options={{
                    search: false,
                    toolbar: false,
                    selection: false,
                    paging: false,
                    headerStyle: {
                        fontSize: "1.5rem",
                        height: 52,
                        padding: theme.spacing(0, 1),
                        whiteSpace: "nowrap",
                        zIndex: 0,
                    },
                }}
                style={{width: leftTableWidth}}
            />
            <AvantVerticalDivider style={{zIndex: 1}} />
            <Box onScroll={handleScroll} overflow={"overlay"} width={"100%"}>
                {/* Box component doesn't have the ref property */}
                <div
                    ref={leftRef}
                    style={{
                        ...useScrollEffectStyles,
                        backgroundImage: "linear-gradient(90def, rgba(0,0,0,0.43) 0%. rgba(255,255,255,0.00) 100%)",
                        opacity: 0,
                        zIndex: 1,
                    }}
                />
                <div
                    ref={rightRef}
                    style={{
                        right: 0,
                        ...useScrollEffectStyles,
                        backgroundImage: "linear-gradient(270deg, rgba(0,0,0,0.43) 0%, rgba(255,255,255,0.00) 100%)",
                        opacity: 0.25,
                        zIndex: 1,
                    }}
                />

                <MaterialTable
                    columns={columns.right}
                    data={reportTableRows}
                    onRowClick={onRowClick}
                    options={{
                        search: false,
                        toolbar: false,
                        paging: false,
                        tableLayout: "auto",
                        headerStyle: {
                            fontSize: "1.5rem",
                            height: 52,
                            padding: theme.spacing(0, 1),
                            whiteSpace: "nowrap",
                            zIndex: 0,
                        },
                        rowStyle: (data) => ({
                            backgroundColor: (data.tableData.id % 2 === 0) ? AvantColors.REPORT_BACKGROUND_GRAY_DARK : "#FFF",
                            '&:hover': {
                                cursor: "none !important",
                            }
                        })
                    }}
                />
            </Box>
        </Box>
    )
})

export default TestTakerSortableTable
